import { Loader2, Sprout } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import {
  useEstimateMutation,
  useExpandProjectMutation,
} from "@/entities/projects/queries";
import type { EstimateOut, ProjectOut } from "@/entities/projects/types";
import { ApiError } from "@/shared/api/http";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { formatNumber } from "@/shared/lib/format";

export interface ExpandDialogProps {
  project: ProjectOut;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExpandDialog({
  project,
  open,
  onOpenChange,
}: ExpandDialogProps): JSX.Element {
  const newDepth = (project.expansion_depth + 1) as 0 | 1 | 2;

  const expandM = useExpandProjectMutation();
  const estimateM = useEstimateMutation();

  // Считаем оценку «дополнительных» вызовов: estimate с НОВОЙ глубиной
  // минус estimate с текущей. Wordstat-кэш на это уже отвечает hit'ами.
  const [diff, setDiff] = React.useState<{
    calls: number;
    cost: number;
  } | null>(null);
  const [estimating, setEstimating] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setEstimating(true);
    Promise.all([
      estimateM.mutateAsync({
        target_phrases: project.target_phrases,
        expansion_depth: project.expansion_depth as 0 | 1 | 2,
        dynamics_top_k: project.dynamics_top_k,
      }),
      estimateM.mutateAsync({
        target_phrases: project.target_phrases,
        expansion_depth: newDepth,
        dynamics_top_k: project.dynamics_top_k,
      }),
    ])
      .then(([prev, next]: [EstimateOut, EstimateOut]) => {
        if (cancelled) return;
        setDiff({
          calls: Math.max(0, next.total_calls - prev.total_calls),
          cost: Math.max(0, next.cost_rub - prev.cost_rub),
        });
      })
      .catch(() => {
        if (!cancelled) setDiff(null);
      })
      .finally(() => {
        if (!cancelled) setEstimating(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, project.expansion_depth, project.target_phrases, project.dynamics_top_k]);

  const onConfirm = (): void => {
    expandM.mutate(
      { id: project.id, body: { new_depth: newDepth } },
      {
        onSuccess: () => {
          toast.success("Расширение запущено");
          onOpenChange(false);
        },
        onError: (e) =>
          toast.error(
            e instanceof ApiError ? e.message : "Не удалось расширить",
          ),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Расширить семантику</DialogTitle>
          <DialogDescription>
            Углубим сбор с уровня <b>{project.expansion_depth}</b> до{" "}
            <b>{newDepth}</b>. Wordstat-кэш покроет повторы — платим только
            за новые сиды.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Дополнительно
            </div>
            {estimating ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Считаю прогноз…
              </div>
            ) : diff ? (
              // 1 вызов topRequests возвращает ~80 уникальных фраз после
              // dedup + фильтров (PHRASES_PER_TOP_CALL в cost_calculator.py).
              // Показываем оба числа: вызовы — чтобы понимать счётчик квоты,
              // фразы — чтобы было «человечное» представление о масштабе.
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Stat
                  label="вызовов API"
                  value={`~${formatNumber(diff.calls)}`}
                />
                <Stat
                  label="новых фраз"
                  value={`~${formatNumber(diff.calls * 80)}`}
                />
                <Stat label="стоимость" value={`~${diff.cost.toFixed(2)} ₽`} />
                <Stat label="время" value="~30-60 сек" />
              </div>
            ) : (
              <div className="text-muted-foreground">Прогноз недоступен</div>
            )}
          </div>

          <ul className="space-y-1.5 text-xs text-muted-foreground">
            <li>
              • Старые кластеры пересоберутся на расширенном наборе фраз.
            </li>
            <li>
              • Аналитики (видео / посты / лидмагниты / полный) станут
              устаревшими — нажми «Регенерировать идеи» вручную после.
            </li>
            <li>
              • Если уже расширял этот проект — повтор бесплатный (кэш).
            </li>
          </ul>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={expandM.isPending}
            className="border-white/15 bg-white/[0.04]"
          >
            Отмена
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={expandM.isPending}
            className="bg-gradient-primary text-white shadow-glow-purple"
          >
            {expandM.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sprout className="h-4 w-4" />
            )}
            Запустить расширение
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="font-mono text-sm font-bold text-white">{value}</div>
    </div>
  );
}
